export const pharmacologyModule = {
  id: "pharmacology",
  title: "Thyroid Pharmacology",
  sections: [
    {
      type: "text",
      title: "Thionamides: Carbimazole, Methimazole & PTU",
      content: `
        <p>Thionamides are the mainstay of medical treatment for hyperthyroidism. They block thyroid hormone synthesis by inhibiting <span class="glossary-term">thyroid peroxidase (TPO)</span>.</p>

        <div class="highlight-box">
          <p><strong>Carbimazole</strong> (preferred in the UK)</p>
          <ul>
            <li>A prodrug — rapidly converted to <strong>methimazole</strong> in the body</li>
            <li>Mechanism: inhibits TPO → blocks organification and coupling → no T3/T4 synthesis</li>
            <li>Onset of effect: 2–4 weeks (existing stored hormone must first be depleted)</li>
            <li>Two treatment regimens for Graves' disease:
              <ul>
                <li><span class="purple-term">Titration regimen</span>: start high (20–40 mg/day), reduce dose to maintain euthyroidism; 12–18 months; lower side-effect burden</li>
                <li><span class="purple-term">Block-replace regimen</span>: high-dose thionamide + levothyroxine simultaneously; avoids dose-adjustment visits; same relapse rate; more side effects</li>
              </ul>
            </li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>PTU (propylthiouracil)</strong></p>
          <ul>
            <li>Inhibits TPO <strong>AND</strong> peripheral deiodinase type 1 (blocks T4→T3 conversion) — faster T3 reduction</li>
            <li>Preferred in:
              <ul>
                <li><strong>First trimester of pregnancy</strong> (methimazole teratogenic in 1st trimester — aplasia cutis, choanal atresia)</li>
                <li><strong>Thyroid storm</strong> (dual mechanism lowers T3 faster)</li>
                <li>Methimazole intolerance</li>
              </ul>
            </li>
            <li>Switch to carbimazole/methimazole at 2nd trimester (PTU risk of hepatotoxicity with prolonged use)</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Side effects — thionamides</strong></p>
          <ul>
            <li><span class="purple-term">Agranulocytosis</span> (most serious) — 0.1–0.5% risk; onset typically 3–12 weeks; presents as sore throat, fever, mouth ulcers; action: <strong>stop drug immediately, check FBC, broad-spectrum antibiotics, G-CSF</strong>; do NOT re-challenge with same drug class</li>
            <li>Rash/urticaria (~5%) — can switch between agents if mild</li>
            <li>Arthralgia/arthritis</li>
            <li>Hepatotoxicity — PTU &gt; methimazole (PTU: hepatocellular; methimazole: cholestatic)</li>
            <li><span class="purple-term">ANCA-positive vasculitis</span> — PTU-specific; pulmonary haemorrhage + GN (similar to ANCA vasculitis); stop PTU</li>
            <li>Hypothyroidism from over-treatment</li>
          </ul>
        </div>

        <div class="mnemonic-card">
          <div class="mnemonic-title">Agranulocytosis Warning — Key Counselling Point</div>
          <div class="mnemonic-content">All patients starting thionamides must be warned: <strong>"If you develop a sore throat or fever, stop the drug immediately and attend A&E for a blood count."</strong> Routine FBC monitoring does NOT reliably predict agranulocytosis — the onset is sudden.</div>
        </div>
      `,
    },
    {
      type: "question",
      text: "A patient on carbimazole calls to say she has had a sore throat and fever for 2 days. What is the most important immediate step?",
      options: [
        "Advise paracetamol and review in 48 hours",
        "Stop carbimazole immediately and arrange urgent FBC — agranulocytosis must be excluded",
        "Switch to PTU and reassess in 1 week",
        "Check TSH to ensure she is not hypothyroid",
      ],
      correct: 1,
      explanation:
        "Sore throat and fever in a patient on a thionamide is agranulocytosis until proven otherwise. Agranulocytosis (neutrophil count <0.5×10⁹/L) is the most dangerous side effect of thionamides, making the patient highly susceptible to life-threatening infections. The drug must be stopped immediately and FBC checked urgently. If agranulocytosis is confirmed, the patient needs hospital admission, broad-spectrum antibiotics (neutropenic sepsis), and G-CSF. They must never be re-challenged with any thionamide.",
    },
    {
      type: "question",
      text: "A patient on long-term PTU develops haematuria and is found to have ANCA-positive serology and a rapidly rising creatinine. What is the diagnosis and what should be done?",
      options: [
        "Hashimoto's thyroiditis causing renal involvement — continue PTU and add prednisolone",
        "PTU-induced ANCA-positive vasculitis — stop PTU immediately; immunosuppression if severe",
        "Methimazole-induced cholestatic hepatitis affecting the kidneys",
        "Coincidental IgA nephropathy — continue PTU and refer nephrology",
      ],
      correct: 1,
      explanation:
        "PTU (not methimazole/carbimazole) is specifically associated with ANCA-positive vasculitis — a rare but serious complication. PTU induces anti-MPO and anti-PR3 antibodies in some patients, causing a vasculitic syndrome identical to idiopathic ANCA vasculitis: GN, pulmonary haemorrhage, skin vasculitis. Management: stop PTU immediately; mild cases resolve; severe cases (RPGN, pulmonary haemorrhage) require immunosuppression (steroids + cyclophosphamide). Switch to RAI or surgery for hyperthyroidism.",
    },
    {
      type: "question",
      text: "In Graves' disease, the titration regimen involves reducing the thionamide dose over 12–18 months. Why does the drug take 2–4 weeks to have clinical effect despite immediate enzyme blockade?",
      options: [
        "Thionamides need 2–4 weeks to achieve therapeutic blood levels",
        "The thyroid has a large store of pre-formed thyroid hormone in colloid — synthesis is blocked but existing stores must be depleted first",
        "TSH levels must fall before any clinical improvement occurs",
        "The drug requires hepatic activation that takes weeks in Graves' patients",
      ],
      correct: 1,
      explanation:
        "Thionamides block TPO and halt new thyroid hormone synthesis almost immediately. However, the thyroid follicles store 2–3 months' worth of pre-formed thyroglobulin in the colloid. This existing reservoir continues to be hydrolysed and released despite enzyme blockade. Clinical improvement only occurs as this stored hormone is gradually depleted — typically over 2–4 weeks. This is also why the thyroid gland cannot respond instantly to thionamide treatment.",
    },
    {
      type: "text",
      title: "Radioactive Iodine (I-131) & Iodine Preparations",
      content: `
        <p><span class="glossary-term">Radioactive iodine (I-131)</span> is an oral treatment that selectively destroys thyroid tissue through beta radiation. It is used for both benign thyroid disease and thyroid cancer.</p>

        <div class="highlight-box">
          <p><strong>Indications</strong></p>
          <ul>
            <li>Graves' disease (definitive treatment)</li>
            <li>Toxic multinodular goitre and toxic adenoma</li>
            <li>Thyroid cancer — <strong>ablation</strong> of residual thyroid tissue after thyroidectomy (PTC, FTC)</li>
            <li>Treatment of metastatic differentiated thyroid cancer</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Pre-treatment considerations</strong></p>
          <ul>
            <li>Render the patient <strong>euthyroid</strong> with thionamides first (prevents thyroid storm from radiation-induced release)</li>
            <li>Stop antithyroid drugs <strong>5–7 days before</strong> RAI (thionamides reduce RAI uptake)</li>
            <li>Ensure adequate iodine uptake (avoid high-iodine foods, contrast dye, amiodarone)</li>
            <li>For thyroid cancer RAI: maximise TSH stimulation (<span class="purple-term">TSH &gt;30 mIU/L</span>) — either by levothyroxine withdrawal (4–6 weeks) or recombinant TSH (thyrotropin alfa) injection</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Contraindications & side effects</strong></p>
          <ul>
            <li>Contraindications: <strong>pregnancy, breastfeeding</strong>; active moderate-severe Graves' orbitopathy (relative — may worsen); patient unable to follow radiation precautions</li>
            <li>Post-treatment hypothyroidism — expected and desirable in cancer; in Graves' treated with levothyroxine</li>
            <li>Transient radiation thyroiditis (pain, release of hormone — cover with beta-blocker)</li>
            <li>Radiation precautions: avoid close contact with pregnant women and children for ~1–2 weeks; avoid shared bodily fluids</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Iodine preparations</strong></p>
          <ul>
            <li><strong>Lugol's solution</strong> (potassium iodide): large iodine load → Wolff-Chaikoff effect → transiently blocks hormone release; reduces gland vascularity
              <ul>
                <li>Uses: <em>pre-operative thyroid preparation</em> (firmer, less vascular gland, reduces intraoperative bleeding) and <em>thyroid storm</em> (must give AFTER antithyroid drug by ≥1 hour)</li>
              </ul>
            </li>
            <li><strong>Potassium iodide (KI) tablets</strong>: <em>nuclear emergency</em> — saturates the thyroid with stable iodine → prevents uptake of radioactive iodine fallout; protects the thyroid only; given to the public in radiation disasters</li>
          </ul>
        </div>
      `,
    },
    {
      type: "question",
      text: "A patient with Graves' disease is given Lugol's iodine 30 minutes before their antithyroid drug during preparation for thyroid storm. Why is this the wrong order?",
      options: [
        "Lugol's iodine should be given intravenously, not orally",
        "Giving iodide before blocking synthesis with an antithyroid drug provides excess substrate and can transiently worsen hormone production (Jod-Basedow risk)",
        "Lugol's iodine is not used in thyroid storm — it is only for pre-operative preparation",
        "The timing does not matter as long as both drugs are given within 2 hours",
      ],
      correct: 1,
      explanation:
        "Lugol's iodine must always be given at least 1 hour AFTER the antithyroid drug (PTU or carbimazole). If iodide is given first, the sudden iodine load can be used as substrate by the uninhibited thyroid to briefly synthesise even more hormone before the Wolff-Chaikoff block kicks in — this is the Jod-Basedow effect. Once the TPO is blocked by the antithyroid drug, the iodide load then effectively inhibits hormone release (Wolff-Chaikoff). The correct sequence in thyroid storm: PTU → 1 hour wait → Lugol's iodide.",
    },
    {
      type: "question",
      text: "A patient with papillary thyroid carcinoma requires radioactive iodine ablation. Levothyroxine has been withdrawn for 4 weeks. What must the TSH level be before administering I-131, and why?",
      options: [
        "TSH must be <0.1 mIU/L — low TSH increases NIS expression in cancer cells",
        "TSH must be >30 mIU/L — high TSH is needed to maximally stimulate NIS expression in cancer cells to allow adequate I-131 uptake",
        "TSH does not matter — I-131 uptake is independent of TSH in cancer cells",
        "TSH must be 0.5–2 mIU/L — the therapeutic range for post-operative patients",
      ],
      correct: 1,
      explanation:
        "The sodium-iodide symporter (NIS), which transports iodine (and I-131) into thyroid cells, is stimulated by TSH. For RAI ablation of residual thyroid cancer tissue, TSH must be >30 mIU/L to maximally upregulate NIS in the cancer cells, enabling adequate I-131 uptake and ablation. TSH is elevated by either: (1) withdrawing levothyroxine for 4–6 weeks (hypothyroid stimulation), or (2) injecting recombinant human TSH (thyrotropin alfa/Thyrogen) while continuing levothyroxine — the latter avoids symptomatic hypothyroidism.",
    },
    {
      type: "text",
      title: "Levothyroxine & Liothyronine",
      content: `
        <p>Levothyroxine (synthetic T4) is the most widely prescribed thyroid hormone replacement, used in hypothyroidism and as suppressive therapy in thyroid cancer.</p>

        <div class="highlight-box">
          <p><strong>Levothyroxine — dosing principles</strong></p>
          <ul>
            <li>Full replacement dose: <strong>~1.6 mcg/kg/day</strong></li>
            <li>Starting dose in elderly or those with cardiac disease: <strong>25–50 mcg/day</strong>, increasing by 25 mcg every 4–6 weeks (rapid increase → precipitate angina/MI)</li>
            <li>Taken <strong>30–60 minutes before food</strong> (absorption reduced by food, calcium, iron, antacids)</li>
            <li>Monitor with <strong>TSH at 6–8 weeks</strong> after each dose change; steady state takes ~6 weeks</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Drug interactions</strong></p>
          <ul>
            <li><strong>Reduced absorption</strong> (separate by ≥4 hours): calcium carbonate, ferrous sulphate, antacids, PPIs, sucralfate, cholestyramine</li>
            <li><strong>Increased metabolism</strong> (need higher levothyroxine dose): rifampicin, phenytoin, carbamazepine, phenobarbitone (enzyme inducers)</li>
            <li><strong>Reduced T4→T3 conversion</strong>: amiodarone, propranolol (high doses)</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Special populations</strong></p>
          <ul>
            <li><strong>Pregnancy</strong>: requirement increases by <span class="purple-term">25–50%</span> from conception; TSH targets: 1st trimester &lt;2.5, 2nd–3rd &lt;3.0 mIU/L; increase dose immediately on confirmation of pregnancy</li>
            <li><strong>Thyroid cancer (TSH suppression)</strong>: target TSH 0.1–0.5 mIU/L (high-risk); 0.5–2.0 (low-risk); over-suppression causes AF and osteoporosis — balance risk vs benefit</li>
            <li><strong>Elderly</strong>: target TSH in the normal range; avoid suppression (↑AF, ↑fracture risk)</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Liothyronine (T3 — synthetic triiodothyronine)</strong></p>
          <ul>
            <li>Shorter half-life (~8 hours vs 7 days for T4); faster onset</li>
            <li>Indications:
              <ul>
                <li><strong>Myxoedema coma</strong>: IV T3 (or T4) given urgently due to faster onset</li>
                <li><strong>Thyroid cancer RAI preparation</strong>: T3 withdrawal protocol — 2 weeks off liothyronine (vs 4–6 weeks off levothyroxine) achieves TSH stimulation faster, reducing hypothyroid burden</li>
                <li>Some patients with persistent symptoms on levothyroxine benefit from combination T4+T3</li>
              </ul>
            </li>
            <li>Not suitable for long-term monotherapy (erratic T3 levels; multiple daily doses needed)</li>
          </ul>
        </div>
      `,
    },
    {
      type: "question",
      text: "A 70-year-old patient with severe hypothyroidism and known ischaemic heart disease needs levothyroxine replacement. What is the correct approach to starting therapy?",
      options: [
        "Start at full replacement dose (1.6 mcg/kg/day) to correct hypothyroidism rapidly",
        "Start at 25–50 mcg/day and increase slowly by 25 mcg every 4–6 weeks to avoid precipitating cardiac ischaemia",
        "Avoid levothyroxine in ischaemic heart disease — the risks outweigh the benefits",
        "Start with liothyronine (T3) as it has a faster onset and shorter duration if adverse effects occur",
      ],
      correct: 1,
      explanation:
        "In elderly patients and those with ischaemic heart disease, rapid correction of hypothyroidism with full-dose levothyroxine can precipitate angina, myocardial infarction, or arrhythmias (thyroid hormone increases heart rate and myocardial oxygen demand). The principle is 'start low, go slow' — begin at 25–50 mcg/day and increase by 25 mcg every 4–6 weeks, with TSH checked 6–8 weeks after each change. Liothyronine (T3) is actually more arrhythmogenic and is less suitable.",
    },
    {
      type: "question",
      text: "A woman with hypothyroidism on stable levothyroxine 125 mcg/day announces she is 8 weeks pregnant. Her TSH is 3.8 mIU/L. What should be done?",
      options: [
        "Continue the same dose — TSH is within the normal range and she is stable",
        "Increase the levothyroxine dose immediately (by ~25–50%) and recheck TSH in 4 weeks — requirements increase significantly in pregnancy",
        "Switch to PTU — levothyroxine crosses the placenta and causes fetal hypothyroidism",
        "Reduce the dose — the fetal thyroid produces its own hormone from 12 weeks",
      ],
      correct: 1,
      explanation:
        "Levothyroxine requirements increase by 25–50% in pregnancy, starting from conception. This is due to increased TBG production (oestrogen effect), placental T4 transfer to fetus, and increased plasma volume. TSH targets are stricter: <2.5 mIU/L in the 1st trimester; <3.0 mIU/L in 2nd–3rd. A TSH of 3.8 mIU/L is already above target — the dose must be increased immediately. Inadequately treated maternal hypothyroidism is associated with miscarriage, placental abruption, and impaired fetal neurodevelopment.",
    },
    {
      type: "text",
      title: "Drug-Induced Thyroid Disease",
      content: `
        <p>Several commonly used drugs have profound effects on thyroid function — either causing new thyroid disease or complicating interpretation of thyroid function tests.</p>

        <div class="highlight-box">
          <p><strong>Amiodarone — the most complex drug</strong></p>
          <ul>
            <li>Contains ~37% iodine by weight; lipophilic with a very long half-life (40–55 days)</li>
            <li>Effects on TFTs in all patients: ↑T4 (blocks T4→T3 conversion + blocks T3 entry into cells), ↑rT3, <span class="purple-term">↓T3</span> — these are expected and do NOT indicate disease</li>
            <li><strong>Amiodarone-induced hypothyroidism (AIH)</strong>:
              <ul>
                <li>Wolff-Chaikoff effect (failure to escape); more common in iodine-sufficient areas and anti-TPO positive patients</li>
                <li>Treatment: <strong>levothyroxine while continuing amiodarone</strong> (often cannot stop amiodarone safely)</li>
              </ul>
            </li>
            <li><strong>Amiodarone-induced thyrotoxicosis (AIT)</strong>:
              <ul>
                <li><span class="purple-term">Type 1 (excess iodine in abnormal thyroid)</span>: underlying nodular disease or latent Graves'; excess iodine fuels synthesis; USS shows increased vascularity; RAI uptake low; treatment: <strong>carbimazole + potassium perchlorate</strong> (blocks NIS)</li>
                <li><span class="purple-term">Type 2 (destructive thyroiditis in normal thyroid)</span>: most common; amiodarone directly toxic to follicular cells → inflammatory release; USS shows decreased vascularity; treatment: <strong>prednisolone</strong>; often self-limiting</li>
                <li>Mixed type: features of both; difficult to distinguish (colour Doppler USS helps)</li>
              </ul>
            </li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Other important drug-induced thyroid effects</strong></p>
          <ul>
            <li><strong>Lithium</strong>: inhibits thyroid hormone release and reduces NIS expression → <span class="purple-term">hypothyroidism</span> (25–40% on long-term lithium) and goitre (50%); check TFTs every 6 months; treat with levothyroxine</li>
            <li><strong>Immune checkpoint inhibitors</strong> (pembrolizumab, nivolumab, ipilimumab): immune-mediated thyroiditis → <em>thyrotoxic phase</em> (weeks 1–6) → <em>hypothyroidism</em> (most patients); treat thyrotoxic phase with beta-blocker; hypothyroid phase with levothyroxine; usually do NOT require steroids unless severe systemic immune-related AE</li>
            <li><strong>Interferon-α</strong> (used in hepatitis C, melanoma): triggers autoimmune thyroid disease (Hashimoto's or Graves') in ~10% due to immune activation; anti-TPO often positive; check TFTs every 3 months during treatment</li>
            <li><strong>Tyrosine kinase inhibitors</strong> (sunitinib, sorafenib, cabozantinib): hypothyroidism via direct thyroid damage + altered levothyroxine metabolism; often require levothyroxine dose increase</li>
          </ul>
        </div>

        <div class="mnemonic-card">
          <div class="mnemonic-title">AIT Type 1 vs Type 2</div>
          <div class="mnemonic-content"><strong>Type 1</strong> — 1 nodule/abnormal thyroid + excess iodine = Too much SYNTHESIS → treat with Carbimazole + Perchlorate<br/><strong>Type 2</strong> — 2 = normal thyroid + DESTRUCTION (thyroiditis) → treat with Prednisolone (steroids)</div>
        </div>
      `,
    },
    {
      type: "question",
      text: "A patient with AF on long-term amiodarone develops thyrotoxicosis. Ultrasound shows decreased thyroid vascularity. What type of amiodarone-induced thyrotoxicosis is this and how is it treated?",
      options: [
        "AIT Type 1 — treat with carbimazole and potassium perchlorate to block iodine uptake",
        "AIT Type 2 — treat with prednisolone; carbimazole is not effective as it is a destructive (not synthetic) process",
        "Amiodarone-induced hypothyroidism — add levothyroxine while continuing amiodarone",
        "Graves' disease triggered by amiodarone — treat with radioactive iodine",
      ],
      correct: 1,
      explanation:
        "Decreased vascularity on colour Doppler USS is characteristic of AIT Type 2 — a destructive thyroiditis where amiodarone directly damages follicular cells, releasing pre-formed hormone. Since the process is inflammatory and destructive (not active synthesis), antithyroid drugs (carbimazole) are ineffective. Prednisolone (30–40 mg/day) is first-line and usually effective. AIT Type 1 (excess iodine driving synthesis in an abnormal gland) shows increased vascularity and responds to carbimazole + potassium perchlorate.",
    },
    {
      type: "question",
      text: "A patient on pembrolizumab for melanoma has a TSH of 45 mIU/L and fT4 of 6 pmol/L (low). She feels fatigued with cold intolerance. What is the likely diagnosis and management?",
      options: [
        "Primary hypothyroidism from Hashimoto's disease coinciding with cancer treatment — stop pembrolizumab",
        "Immune checkpoint inhibitor-induced thyroiditis progressing to hypothyroidism — continue pembrolizumab and start levothyroxine",
        "Pembrolizumab-induced hyperthyroidism — start antithyroid drugs",
        "Paraneoplastic hypothyroidism from melanoma — treat the underlying tumour",
      ],
      correct: 1,
      explanation:
        "Immune checkpoint inhibitors (anti-PD-1/PD-L1 agents like pembrolizumab) commonly cause immune-mediated thyroiditis. The typical course is: thyrotoxic phase (weeks 1–6, from follicular cell destruction releasing stored hormone) → hypothyroid phase (as follicular cells are destroyed). This patient is in the hypothyroid phase. Management: continue pembrolizumab (it is the cancer treatment) and start levothyroxine replacement. High-dose steroids are not usually needed for isolated thyroid immune-related adverse events.",
    },
    {
      type: "text",
      title: "Drugs for Advanced Thyroid Cancer",
      content: `
        <p>Differentiated thyroid cancers (PTC, FTC) that are refractory to radioactive iodine, and medullary thyroid carcinoma, require systemic targeted therapies.</p>

        <div class="highlight-box">
          <p><strong>RAI-refractory differentiated thyroid cancer (PTC/FTC)</strong></p>
          <ul>
            <li><strong>Lenvatinib</strong> (VEGFR 1–3, RET, FGFR, PDGFR inhibitor) — first-line; significantly extends PFS</li>
            <li><strong>Sorafenib</strong> (VEGFR, BRAF, RET inhibitor) — first-line alternative; well-established</li>
            <li>Side effects of VEGFR inhibitors: hypertension, fatigue, diarrhoea, hand-foot skin reaction, hepatotoxicity, QTc prolongation</li>
            <li>For <span class="purple-term">BRAF V600E-positive PTC/ATC</span>: <strong>dabrafenib + trametinib</strong> (BRAF + MEK inhibitors) — substantial responses, especially in anaplastic thyroid carcinoma</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Medullary thyroid carcinoma (MTC)</strong></p>
          <ul>
            <li><strong>Vandetanib</strong> — RET + VEGFR2/3 + EGFR inhibitor; first-line for advanced/metastatic MTC; QTc prolongation risk (ECG monitoring essential)</li>
            <li><strong>Cabozantinib</strong> — RET + VEGFR2 + MET + AXL inhibitor; first-line (approved in 2012); second-line after vandetanib</li>
            <li><strong>Selpercatinib</strong> and <strong>pralsetinib</strong> — highly selective RET inhibitors; effective in RET-mutant MTC with fewer off-target side effects; also for RET-fusion PTC</li>
            <li>RAI is <strong>not</strong> used in MTC (C cells lack NIS)</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Selenium</strong></p>
          <ul>
            <li>Supplementation (200 mcg/day for 6 months) has been shown to reduce anti-TPO antibody titres and improve quality of life in Hashimoto's thyroiditis</li>
            <li>Reduces progression of mild Graves' orbitopathy to moderate-severe disease</li>
            <li>Mechanism: selenoproteins (including deiodinases and glutathione peroxidase) reduce oxidative stress in the thyroid; selenium is incorporated into antioxidant enzymes</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Cholestyramine (as adjunct in thyrotoxicosis)</strong></p>
          <ul>
            <li>Bile acid sequestrant that also binds thyroid hormones in the gut</li>
            <li>Interrupts enterohepatic recirculation of T3/T4 → lowers serum T3/T4</li>
            <li>Used as adjunct in thyroid storm or severe thyrotoxicosis alongside other treatments</li>
          </ul>
        </div>
      `,
    },
    {
      type: "question",
      text: "A patient with metastatic medullary thyroid carcinoma has a RET M918T mutation (MEN2B-associated). Which drug class is most appropriate for systemic treatment?",
      options: [
        "Radioactive iodine — effective in all thyroid cancers",
        "Selective RET kinase inhibitors (selpercatinib or pralsetinib) — target the driver mutation directly",
        "BRAF + MEK inhibitors (dabrafenib + trametinib) — target the BRAF V600E mutation",
        "VEGF antibodies (bevacizumab) — anti-angiogenic for thyroid cancers",
      ],
      correct: 1,
      explanation:
        "MTC is driven by RET mutations (germline in hereditary cases, somatic in sporadic). RET M918T (codon 918) is the most aggressive and is pathognomonic of MEN2B. Selective RET inhibitors — selpercatinib and pralsetinib — are highly effective in RET-mutant MTC with favourable response rates and fewer off-target side effects than vandetanib/cabozantinib. RAI is ineffective in MTC (C cells lack NIS). BRAF+MEK inhibitors are used for BRAF V600E+ PTC/ATC, not for RET-mutant MTC.",
    },
    {
      type: "question",
      text: "Why is vandetanib used with caution and ECG monitoring in MTC?",
      options: [
        "It causes hypothyroidism by blocking the sodium-iodide symporter",
        "It causes QTc prolongation, which can lead to torsades de pointes and sudden cardiac death",
        "It triggers autoimmune thyroid disease by activating T cells",
        "It inhibits levothyroxine absorption, requiring dose adjustment",
      ],
      correct: 1,
      explanation:
        "Vandetanib is a RET + VEGFR2/3 + EGFR inhibitor used in advanced MTC. A significant side effect is QTc interval prolongation, which can precipitate torsades de pointes — a potentially fatal ventricular arrhythmia. ECG monitoring is mandatory before and during treatment. Vandetanib is contraindicated with other QTc-prolonging drugs. Patients with baseline QTc >450 ms should not receive it. Electrolyte monitoring (K⁺, Mg²⁺) is essential as hypokalaemia and hypomagnesaemia worsen QTc.",
    },
  ],
};
